// cspell:ignore rerank

/** 模型能力模态键，与模型厂商创建表单保持一致。 */
export const MODEL_CAPABILITY_MODALITY_KEYS = [
  'textGeneration',
  'toolCall',
  'imageUnderstanding',
  'imageGeneration',
  'audioUnderstanding',
  'audioGeneration',
  'videoUnderstanding',
  'videoGeneration',
  'embedding',
  'rerank',
] as const;

/** 模型能力模态类型。 */
export type ModelCapabilityModalityKey = (typeof MODEL_CAPABILITY_MODALITY_KEYS)[number];

/** 模型能力下拉选项。 */
export interface ModelCapabilityOption {
  /** 模型能力中文名称。 */
  label: string;
  /** 模型能力键。 */
  value: ModelCapabilityModalityKey;
}

/** 模型能力中文映射。 */
export const MODEL_CAPABILITY_LABEL_MAP: Record<ModelCapabilityModalityKey, string> = {
  textGeneration: '文本生成',
  toolCall: '工具调用',
  imageUnderstanding: '图片理解',
  imageGeneration: '图片生成',
  audioUnderstanding: '音频理解',
  audioGeneration: '音频生成',
  videoUnderstanding: '视频理解',
  videoGeneration: '视频生成',
  embedding: '向量嵌入',
  rerank: '重排序',
};

/** 模型能力选项，与公共能力键保持同源。 */
export const MODEL_CAPABILITY_OPTIONS: ModelCapabilityOption[] = MODEL_CAPABILITY_MODALITY_KEYS.map((value) => ({
  label: MODEL_CAPABILITY_LABEL_MAP[value],
  value,
}));

/** 模型未声明能力时使用的默认文本生成能力。 */
export const DEFAULT_MODEL_CAPABILITY: ModelCapabilityModalityKey = 'textGeneration';

/** 历史模型分类到统一能力键的兼容映射，仅在数据入口归一化时使用。 */
const LEGACY_MODEL_CAPABILITY_MAP: Record<string, ModelCapabilityModalityKey> = {
  llm: 'textGeneration',
  embeddings: 'embedding',
  images: 'imageGeneration',
  imagesRecognition: 'imageUnderstanding',
  tts: 'audioGeneration',
  stt: 'audioUnderstanding',
};

/** 模型能力布尔标记集合。 */
export type ModelCapabilityFlags = Partial<Record<ModelCapabilityModalityKey, boolean>>;

/** 提取模型能力中已启用的模态列表。 */
export function getEnabledModelCapabilities(capabilities?: ModelCapabilityFlags): ModelCapabilityModalityKey[] {
  if (!capabilities) return [];
  return MODEL_CAPABILITY_MODALITY_KEYS.filter((key) => Boolean(capabilities[key]));
}

/** 将接口或历史数据中的模型能力数组归一化为公共能力键。 */
export function normalizeModelCapabilityKeys(value: unknown): ModelCapabilityModalityKey[] {
  if (!Array.isArray(value)) return [];

  /** 公共能力键集合，用于过滤后端返回的未知能力。 */
  const modalityKeySet = new Set<string>(MODEL_CAPABILITY_MODALITY_KEYS);
  return Array.from(
    new Set(
      value
        .map((item) => String(item || '').trim())
        .map((item) => LEGACY_MODEL_CAPABILITY_MAP[item] || item)
        .filter((item): item is ModelCapabilityModalityKey => modalityKeySet.has(item)),
    ),
  );
}

/** 获取模型能力中文名称。 */
export function getModelCapabilityLabel(category?: ModelCapabilityModalityKey): string {
  return category ? MODEL_CAPABILITY_LABEL_MAP[category] : '-';
}
